// A directory-backed people picker: search colleagues by display name, store their emails.
// used for agenda mentions/sharing so the UI shows names, not raw addresses.
import { useMemo, useState } from 'react';
import { useDirectory } from '../features/directory.js';
import { fold, hasText } from '../lib/search.js';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function PeoplePicker({
  value,
  onChange,
  disabled = false,
  placeholder = 'Type a name…',
  ariaLabel = 'Add people',
}: {
  value: string[];
  onChange: (emails: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  /** the input's name; pass the visible label text so it matches (the placeholder empties once someone is chosen). */
  ariaLabel?: string;
}) {
  const { data } = useDirectory();
  const users = data?.users ?? [];
  const byEmail = useMemo(() => new Map(users.map((u) => [u.email.toLowerCase(), u.displayName])), [users]);
  const nameOf = (email: string) => byEmail.get(email.toLowerCase()) ?? email;

  const [q, setQ] = useState('');
  const [openList, setOpenList] = useState(false);
  const selected = new Set(value.map((v) => v.toLowerCase()));
  const query = fold(q.trim());

  const matches = useMemo(
    () =>
      users
        .filter((u) => !u.aliasOf) // a merged-away address is the same person, already listed by their account
        .filter((u) => !selected.has(u.email.toLowerCase()))
        .filter((u) => !query || hasText(query, u.displayName, u.email))
        .slice(0, 8),
    [users, query, value], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const add = (email: string) => {
    const e = email.trim();
    if (e && !selected.has(e.toLowerCase())) onChange([...value, e]);
    setQ('');
  };
  const remove = (email: string) => onChange(value.filter((v) => v.toLowerCase() !== email.toLowerCase()));

  const onKey = (ev: React.KeyboardEvent) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      if (matches.length) add(matches[0]!.email);
      else if (EMAIL_RE.test(q.trim())) add(q.trim());
    } else if (ev.key === 'Backspace' && !q && value.length) {
      remove(value[value.length - 1]!);
    }
  };

  const isEmail = EMAIL_RE.test(q.trim());

  if (disabled) {
    return (
      <div className="ppick ppick-disabled">
        <div className="ppick-box">
          {value.length ? value.map((e) => <span key={e} className="ppick-chip">{nameOf(e)}</span>) : <span className="muted" style={{ padding: '2px 4px' }}>Not shared</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="ppick">
      <div className="ppick-box">
        {value.map((e) => (
          <span key={e} className="ppick-chip">
            {nameOf(e)}
            <button type="button" className="ppick-x" onClick={() => remove(e)} aria-label={`Remove ${nameOf(e)}`} title="Remove">×</button>
          </span>
        ))}
        <input
          className="ppick-input"
          aria-label={ariaLabel}
          value={q}
          placeholder={value.length ? '' : placeholder}
          onChange={(e) => { setQ(e.target.value); setOpenList(true); }}
          onFocus={() => setOpenList(true)}
          onBlur={() => setTimeout(() => setOpenList(false), 120)}
          onKeyDown={onKey}
        />
      </div>
      {openList && (query.length > 0 || matches.length > 0) && (
        <div className="ppick-menu">
          {matches.map((u) => (
            <button key={u.email} type="button" className="ppick-opt" onMouseDown={(e) => { e.preventDefault(); add(u.email); }}>
              <span className="ppick-opt-name">{u.displayName}</span>
              <span className="ppick-opt-email">{u.email}</span>
            </button>
          ))}
          {!matches.length && (isEmail
            ? <button type="button" className="ppick-opt" onMouseDown={(e) => { e.preventDefault(); add(q.trim()); }}>Add “{q.trim()}”</button>
            : <div className="ppick-empty">No matches{query ? '' : ' - type a name'}</div>)}
        </div>
      )}
    </div>
  );
}
